import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import useSnackBarContext from '@contexts/snackBarContext'
import { colors, fontSize, size } from '@styles/theme'
import { useAuthContext } from '@contexts/authContext'
import useModalContext from '@contexts/modalContext'
import useStateRef from 'react-usestateref'
import useLoading from '@hooks/useLoading'
import axios from '@utils/webRequests'
import styled from '@emotion/styled'
import { Button, ButtonBase, CircularProgress, LinearProgress } from '@mui/material'
import { PiArrowRightBold, PiCamera, PiImage, PiXBold } from 'react-icons/pi'
import AuthenticatedAvatar from '@elements/AuthenticatedAvatar'
import NewPostQuill from '@forms/new_post_form/NewPostQuill'
import ModalContainer from '@elements/ModalContainer'
import useFileUpload from '@hooks/useFileUpload'

/* TODO:
  4. Add the purple color to themes so you don't have to keep rewriting it
  6. Consider creating a general purpose modal context to manage opening all the universal modals across the app...? 
      - How many are actually universal anyway though? Are there downsides to keeping the components higher in the render tree? Like, an inability to lazy load them?
  7. Consider creating base modal styles for reuse. This is a bit hard, because we have a lot of use cases.
      - consider modals for 'preferences', small displays for selects (like the temp status), the map profile modals, the media viewers, and the regular profile image displays.
*/

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1;
`

const SignupDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: ${size(4)};
  width: 100%;
  height: 100%;
`

const SignupDisplayText = styled.h2`
  margin: 0;
  text-align: center;
  line-height: ${size(5)};
  font-size: ${size(3.75)};
`

const SignupButton = styled(Button)`
  display: flex;
  align-items: center;
  border-radius: 100px;
  color: white;
  font-size: ${fontSize('lg')};
  padding: ${size(1.25)} ${size(3)};
  margin-bottom: 30dvh;
`

// function isWhitespace(ch) {
//   var whiteSpace = false
//   if ((ch == ' ') || (ch == '\t') || (ch == '\n')) {
//     whiteSpace = true;
//   }
//   return whiteSpace;
// }

const NewPostForm = () => {
  const { state: authState } = useAuthContext()
  const isAnonymous = authState?.user?.isAnonymous
  const { addSnackBar } = useSnackBarContext()
  
  const [quill, setQuill, quillRef] = useStateRef()
  const [quillValue, setQuillValue] = useState('') // Gets set to deltas
  const [quillLength, setQuillLength] = useState(1)
  const progressPercent = ((quillLength/280) * 100)
  const progressCalc = progressPercent > 100 ? 100 : progressPercent
  
  const [mediaInput, setmediaInput] = useState(null)
  const containerRef = useRef(null)
  const barRef = useRef(null)
  
  const location = useLocation()
  const navigate = useNavigate()
  const { newPost, registerUser, callNewPostsCallbacks } = useModalContext()
  
  const {
    file,
    handleFileUpload: handleFileStateUpload,
    resetFileState,
    fileUrl,
  } = useFileUpload()
  // For loading image and video on screen
  const [isFileLoaded, setIsFileLoaded]= useState(false)
  // For uploading the file to browser, AND for submitting the upload request to backend (it toggles the same loading display)
  const { isLoading, isLoadingDelayed, setIsLoading } = useLoading(false, 500)

  const [searchParams, setSearchParams] = useSearchParams()
  const replyHandle = searchParams.get('reply_to')
  const replyId = searchParams.get('reply_id')
  const mainPostId = searchParams.get('main_post_id')

  const isReply = !!replyHandle && !!replyId
  const isNotSubmittable = isLoading || (!isFileLoaded && (quillLength <= 1 || progressPercent > 100))

  // Reset state when modal is closed
  useEffect(() => {
    if (!newPost.isOpen) {
      setQuillValue('')
      setQuillLength(1)
      resetFileState()
      setIsFileLoaded(false)
    }
  }, [location.hash, newPost.isOpen])

  const handleFileUpload = (e) => {
    setIsLoading(true)
    handleFileStateUpload(e)
      .then(() => e.target.value = null)
      .finally(() => setIsLoading(false))
  }

  // Opens the camera on load when the "open_camera='true'" search param is passed
  // NOTE: This may not work in all browsers. The internet says it likely won't work in Opera and Firefox
  useEffect(() => {
    // NOTE: The camera variable isn't always initialized when you open this, so it's important
    //       to have the effect listen for the mediaInput as well. That also means you need to
    //       remove "open_camera" from the search params so it doesn't open again when you back navigate
    if (searchParams.get('open_media') === 'true' && mediaInput) {
      // WARNING: Adding the event listener again here SHOULDN'T be necessary, but
      //          the onChange event seems to not happen consistently otherwise (at least in chrome)
      //          Adding this listener on should be fine since it's the same listener being added every time
      //          and we also don't need to de-register it since the modal isn't getting unmounted.
      mediaInput?.addEventListener('change', handleFileUpload)
      mediaInput?.click(handleFileUpload)
      searchParams.delete('open_camera')
      setSearchParams(searchParams, { replace: true })
    }
  }, [newPost.isOpen, mediaInput])

  // Inserts mention when modal opens
  // WARNING: This is basically 'hello world' proof of concept code
  useEffect(() => {
    if (newPost.isOpen && quill) {
      const mentioned = searchParams.get('mention')
      if (mentioned) {
        quill.editor.getModule('mention').insertItem({ value: mentioned, denotationChar: '@' }, true)
      }
    }
  }, [newPost.isOpen, quill])

  // NOTE: The below is used to auto highlight links and hashtags (converting them into links), and that works well BUT it doesn't seem to wrok with quill mention...
  // useEffect(() => {
  //   // Autolink URLs when typing
  //   // debugger
  //   quillRef.current?.editor?.on('text-change', function(delta, oldDelta, source) {
  //     var regex = /https?:\/\/[^\s]+$/;
  //     if(delta.ops.length === 2 && delta.ops[0].retain && isWhitespace(delta.ops[1].insert)) {
  //       var endRetain = delta.ops[0].retain;
  //       var text = quillRef.current.editor.getText().substr(0, endRetain);
  //       var match = text.match(regex);

  //       if(match !== null) {
  //         var url = match[0];

  //         var ops = [];
  //         if(endRetain > url.length) {
  //           ops.push({ retain: endRetain - url.length });
  //         }

  //         ops = ops.concat([
  //           { delete: url.length },
  //           { insert: url, attributes: { link: url } }
  //         ]);

  //         quillRef.current.editor.updateContents({
  //           ops: ops
  //         });
  //       }
  //     }
  //   });
  // }, [quill])

  // useEffect(() => {
  //   // Autolink URLs when typing
  //   // debugger
  //   quillRef.current?.editor?.on('text-change', function(delta, oldDelta, source) {
  //     // var regex = /(^|\s)(#\/\/[^\s]+$)/;
  //     // var regex = /^#[^#\s]+(?: #[^#\s]+)+$/;
  //     var regex = /\B(\#[a-zA-Z]+\b)(?!;)+$/;
  //     if(delta.ops.length === 2 && delta.ops[0].retain && isWhitespace(delta.ops[1].insert)) {
  //       var endRetain = delta.ops[0].retain;
  //       var text = quillRef.current.editor.getText().substr(0, endRetain);
  //       var match = text.match(regex);
  //       if(match !== null) {
  //         var url = match[0];

  //         var ops = [];
  //         if(endRetain > url.length) {
  //           ops.push({ retain: endRetain - url.length });
  //         }

  //         ops = ops.concat([
  //           { delete: url.length },
  //           { insert: url, attributes: { link: url } }
  //         ]);

  //         quillRef.current.editor.updateContents({
  //           ops: ops
  //         });
  //       }
  //     }
  //   });
  // }, [quill])

  /////////////////////////////
  // TODO: We need to delete all the state when the component unmounts.
  //       *** That, or we can put the new form we're going to make further down the rendering tree
  //           and just unmount it with all the stte included at the form level.
  ////////////////////////////

  // Resets position of the bottom bar when the screen size changes (like the keyboard opening)
  // 
  // NOTE: This is running on every single size change and could get very computationally expensive
  //       if not throttled. Fortunately, screen resize events are rare unless a user is doing it actively
  useEffect(() => {
    const onResize = () => {
      const viewport = window.visualViewport
      if (barRef?.current && barRef?.current?.style?.top !== `${viewport.height}px`) {
        barRef.current.style.top = `${viewport.height}px`
      }
      // TODO: either try to get these values dynamically, or at least document what all the pixels values are
      // if (containerRef?.current && containerRef?.current?.style?.height !== `${viewport.height - (69 + 72 + 16)}px`) {
      //   containerRef.current.style.height = `${viewport.height - (69 + 72 + 16)}px`
      // }
      document.body.style.height = viewport.height + 'px'
    }
    window.visualViewport.addEventListener("resize", onResize);
    return () => window.visualViewport.removeEventListener("resize", onResize);
  }, [])

  const createPost = () => {
    if(isNotSubmittable) return;
    setIsLoading(true)

    // Create Form Data for request (necessary for file)
    const formData = new FormData()
    formData.append('file', file)
    formData.append('body', quillRef?.current?.value)
    if (isReply) formData.append('reply_id', replyId)

    // send request
    axios.post(`/api/my/posts`, formData)
      .then((res) => {
        if (res.data) {
          // Check if an afterCreate callback has been saved and call it if it has
          newPost?.callbacks?.afterCreate && newPost.callbacks.afterCreate(res.data, mainPostId)
          callNewPostsCallbacks(res.data)
        }
        setIsLoading(false)
        addSnackBar({ message: 'Post uploaded', kind: 'success' })
        navigate(-1)
      })
      .catch((err) => {
        setIsLoading(false)
        addSnackBar({ message: 'Sorry, we couldn\'t upload your post', kind: 'error' })
        console.error(err)
      })
  }

  const renderHeader = ({ goBack }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: `${size(2)} ${size(1.5)}`, position: 'relative' }}>
      <LinearProgress style={{ position: 'absolute', visibility: isLoadingDelayed ? 'visible' : 'hidden', top: 0, left: 0, width: '100%' }} />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button onClick={goBack} style={{ padding: size(0), minWidth: 0, borderRadius: 100, marginLeft: size(0.75) }}>
          <PiXBold size={size(3)} fill='white' />
        </Button>
        <h3 style={{ display: 'inline-block', marginLeft: size(2) }}>New Post</h3>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {/* <span style={{ fontSize: fontSize('xs'), color: progressPercent > 100 ? 'red' : '#6A23FF', marginRight: size(1), position: 'relative' }}>
          <CircularProgress variant='determinate' value={progressCalc} style={{ color: progressPercent > 100 ? 'red' : '#6A23FF' }} />
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -65%)' }}>
            {280 - quillLength}
          </div>
        </span> */}
        <Button
          variant='contained'
          disabled={isNotSubmittable || isAnonymous}
          onClick={createPost}
          style={{ borderRadius: 100, backgroundColor: '#6A23FF', color: 'white', fontWeight: 'bold', opacity: isNotSubmittable || isAnonymous ? 0.6 : 1 }}>
          {isLoadingDelayed ? 'Loading...' : 'Post'}
        </Button>
      </div>
    </div>
  )

  const renderQuillOrSignup = () => {
    if (isAnonymous) return (
      <SignupDisplay>
        <SignupDisplayText>
          Sign up to<br />create posts
        </SignupDisplayText>
        <SignupButton
          onClick={() => registerUser.open()}
          variant='contained'
          style={{ backgroundColor: colors.statusColors.right_now('primary'), }}>
          Sign up Here&nbsp;<PiArrowRightBold />
        </SignupButton>
      </SignupDisplay>
    ); else return (
      <form style={{ overflow: 'scroll', width: '100%', display: 'flex', flexGrow: 1, paddingRight: size(2) }}>
        <div style={{ flexGrow: 0 }}>
          <AuthenticatedAvatar
            canNavigate={false}
            style={{ marginLeft: size(1.5), marginRight: size(1.5), height: size(5), width: size(5) }}
          />
        </div>
        <div style={{ display: 'flex', flexGrow: 1, position: 'relative', flexDirection: 'column', overflowX: 'hidden', height: '100%' }}>
          {/* For the love of anything, refactor this */}
          {isReply && <span style={{ fontSize: '16px', color: 'grey', padding: `${size(0.5)} 0`, display: 'inline-block' }}>Replying to <span style={{ color: colors.statusColors.right_now('primary'), textDecoration: 'none' }}>@{replyHandle}</span></span> }
          <NewPostQuill
            setQuill={setQuill}
            quillRef={quillRef}
            quillValue={quillValue}
            setQuillValue={setQuillValue}
            setQuillLength={setQuillLength}
          />
          <div
            className={isFileLoaded && 'animation__scaleload'}
            style={{ maxHeight: '50dvh', maxWidth: '100%', position: 'relative', border: file ? '0.5px solid rgba(150,150,150, 0.25)' : 'none', borderRadius: 15, opacity: 0, transform: 'scale(0)', flexShrink: 0, overflowX: 'hidden' }}>
            {file &&
              <ButtonBase
                alt='Remove Uploaded Media'
                onClick={() =>{
                  setIsFileLoaded(false)
                  resetFileState()
                }}
                style={{
                  backgroundColor: 'rgba(150,150,150, 0.75)',
                  borderRadius: 100,
                  padding: size(0.75),
                  position: 'absolute',
                  top: size(1),
                  right: size(1),
                  border: '0.5px solid rgba(165,165,165, 0.75)',
                  zIndex: 1
                }}>
                <PiXBold size={size(2)} />
              </ButtonBase>
            }
            {renderPreview()}
          </div>
        </div>
      </form>
    )
  }

  const renderPreview = () => {
    if (file) {
      if (file?.type?.includes('image')) {
        return (
          <img
            onLoad={(e) => setIsFileLoaded(true)}
            className={isFileLoaded && 'animation__scaleload'}
            alt="Image Uploaded"
            style={{ cursor: 'zoom-in', borderRadius: 4, width: '100%', height: '100%', opacity: 0, transform: 'scale(0)', objectFit: 'cover' }}
            src={fileUrl}
          />
        )
      } else if (file?.type?.includes('video')) {
        return (
          <video
            controls
            muted
            alt="Video Uploaded"
            onLoadedData={(e) => setIsFileLoaded(true)}
            className={isFileLoaded && 'animation__scaleload'}
            style={{ cursor: 'zoom-in', borderRadius: 4, width: '100%', height: '100%', opacity: 0, transform: 'scale(0)', objectFit: 'cover' }}
            src={fileUrl}
          />
        )
      }
    }
  }

  return (
    <ModalContainer renderCustomHeader={renderHeader} open={newPost.isOpen} classNames=''>
      <div ref={containerRef} style={{ width: '100dvw', display: 'flex', flexDirection: 'column', overflow: 'scrollY', height: '100%' }}>
        {renderQuillOrSignup()}
        <div
          ref={barRef}
          style={{
            borderTop: '0.5px solid rgba(255,255,255,0.2)',
            padding: `${size(0.75)} ${size(1)}`,
            position: 'fixed',
            top: window.visualViewport.height,
            left: 0,
            transform: 'translateY(-100%)',
            width: '100%',
            // boxShadow: '-2px 0 6px rgba(106, 35, 255, 0.25)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'black'
          }}>
          {/* <Button
            component='label'
            role={undefined}
            style={{ borderColor: '#6A23FF', minWidth: 0, padding: size(1.5), color: '#6A23FF', backgroundColor: 'rgba(106, 35, 255, 0.1)', position: 'absolute', top: size(-0.75), left: size(1), transform: 'translateY(-100%)', borderRadius: '100px' }}>
            <VisuallyHiddenInput
              type='file'
              accept='video/*,image/*'
              onChange={handleFileUpload}
            />
            <PiPlus fill='#6A23FF' size={size(3)} />
          </Button> */}
          <div>
            <Button
              disabled={isLoading || isAnonymous}
              component='label'
              role={undefined}
              style={{ borderColor: '#6A23FF', minWidth: 0, color: 'white', fontSize: size(1.75), backgroundColor: colors.statusColors.right_now('primary'), marginRight: size(0.5), borderRadius: '100px', padding: `${size(1)} ${size(2)}`, opacity: isLoadingDelayed || isAnonymous ? 0.6 : 1 }}>
              <VisuallyHiddenInput
                ref={node => node && setmediaInput(node)}
                type='file'
                // accept='video/*,image/*'
                accept='image/*'
                onChange={handleFileUpload}
              />
              <PiImage fill='white' size={size(3)} style={{ marginRight: size(0.5) }} />
              Media +
            </Button>
            <Button
              disabled={isLoading || isAnonymous}
              component='label'
              role={undefined}
              style={{ borderColor: '#6A23FF', minWidth: 0, color: 'white', fontSize: size(1.75), backgroundColor: colors.statusColors.right_now('primary'), marginRight: size(0.5), borderRadius: '100px', padding: `${size(1)} ${size(2)}`, opacity: isLoadingDelayed || isAnonymous ? 0.6 : 1 }}>
              <VisuallyHiddenInput
                type='file'
                capture='environment'
                onChange={handleFileUpload}
              />
              <PiCamera fill='white' size={size(3)} style={{ marginRight: size(0.5) }} />
              Camera
            </Button>
          </div>
          <span style={{ fontSize: fontSize('xs'), color: progressPercent > 100 ? 'red' : '#6A23FF', margin: `0 ${size(1)}`, position: 'relative', height: size(5), width: size(5) }}>
            <CircularProgress variant='determinate' value={progressCalc} style={{ color: progressPercent > 100 ? 'red' : '#6A23FF' }} />
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
              {280 - quillLength}
            </div>
          </span>
        </div>
      </div>
    </ModalContainer>
  )
}

export default NewPostForm
